/** Compose display name from Name / Father-Husband / Surname parts. */
export function composeFullName(name, fatherHusbandName, surname) {
  return [name, fatherHusbandName, surname]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Age in whole years from DOB string (YYYY-MM-DD from <input type="date">).
 * Returns null if invalid / empty.
 */
export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

/**
 * Age as years + months from DOB (e.g. "25 years 1 month").
 * Returns null if invalid / empty.
 */
export function ageYearsMonthsFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(String(dob).includes('T') ? dob : `${dob}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  if (birth > today) return null;

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  if (today.getDate() < birth.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return null;

  const yLabel = years === 1 ? '1 year' : `${years} years`;
  const mLabel = months === 1 ? '1 month' : `${months} months`;
  return `${yLabel} ${mLabel}`;
}

/** Format ISO date (YYYY-MM-DD or full ISO datetime) as DD/MM/YYYY for display. */
export function formatDobDisplay(dob) {
  if (!dob) return '—';
  const text = String(dob).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const slash = text.replace(/\s+/g, '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const d = slash[1].padStart(2, '0');
    const m = slash[2].padStart(2, '0');
    return `${d}/${m}/${slash[3]}`;
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const d = String(parsed.getDate()).padStart(2, '0');
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${parsed.getFullYear()}`;
  }
  return text;
}

/** Alias — all UI calendar dates use DD/MM/YYYY. */
export function formatDateDisplay(value) {
  return formatDobDisplay(value);
}

/** Date-time for UI: DD/MM/YYYY, HH:mm */
export function formatDateTimeDisplay(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hh}:${mm}`;
}
